<template>
  <span class="c-tag" :class="theme && `c-tag-${theme}`" :style="rootStyle">
    <slot/>
  </span>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import { HexColor } from '../models/common';
  import { getColorHexStyle, getContrastingColor } from '../utils/style';

  export type Theme = 'neutral'
    | 'info'
    | 'success'
    | 'warning'
    | 'danger'
    | 'neutral-light'
    | 'info-light'
    | 'success-light'
    | 'warning-light'
    | 'danger-light'
    | 'contrast'
    | 'success-contrast'
    | 'neutral-contrast'
    | 'outline';

  const props = defineProps<{
    theme?: Theme;
    color?: HexColor
  }>();

  const rootStyle = computed(() => {
    if (props.color) {
      return {
        ...getColorHexStyle(props.color),
        color: getContrastingColor(props.color),
      };
    }
  });
</script>

<style lang="scss">
  .c-tag {
    display: inline-flex;
    align-items: center;
    line-height: 1.25rem;
    border-radius: $border-radius;
    padding: .25rem;
    gap: .25rem;
    white-space: nowrap;

    &-neutral {
      background: $neutral;
      color: $neutral-0;
    }

    &-info {
      background: $info;
      color: $neutral-0;
    }

    &-success {
      background: $success;
      color: $neutral-900;
    }

    &-warning {
      background: $warning;
      color: $neutral-900;
    }

    &-danger {
      background: $danger;
      color: $neutral-0;
    }

    &-neutral-light {
      color: $neutral-900;
      background: $neutral-200;
    }

    &-info-light {
      background: $info-100;
      color: $info;
    }

    &-success-light {
      background: $success-100;
      color: $success;
    }

    &-warning-light {
      background: $warning-100;
      color: $warning;
    }

    &-danger-light {
      background: $danger-100;
      color: $danger;
    }

    &-contrast {
      color: $neutral-0;
      background: $neutral-1000;
    }

    &-success-contrast {
      background: $success;
      color: $neutral-0;
    }

    &-neutral-contrast {
      background: $neutral-200;
      color: $neutral-900;
    }

    &-outline {
      border: 1px solid $border-color;
      color: $neutral-900;
      background-color: $neutral-0;
    }
  }
</style>
